import { useLayoutEffect, useRef, useState } from 'react'

/** Attribute that marks an element as a measurable chip inside the measure row. */
export const CHIP_OVERFLOW_ITEM = `data-overflow-item`

type UseChipOverflowOptions = {
  /** The clipping container the visible chips render into (must be `relative`). */
  containerRef: React.RefObject<HTMLElement | null>
  /** The hidden measure row that always renders every chip (see ChipMeasureRow). */
  measureRef: React.RefObject<HTMLElement | null>
  /** Total number of chips the caller wants to show. */
  itemCount: number
  /** Gap between chips in px (match the container's gap utility). */
  gap?: number
  /** Width always reserved at the end of the row (e.g. the "+" add chip), in px. */
  reservedWidth?: number
}

/**
 * Shared single-line chip clipping for table cells (tags, pipelines, tasks).
 *
 * Unlike the older estimation-based useVisibleTags, this measures REAL rendered
 * chips, so font, padding, and icon changes can never drift the math. And unlike
 * a slice-in-place approach, it measures a hidden always-complete copy of the
 * chips (ChipMeasureRow), so measuring never changes what is measured: there is
 * no expand/slice protocol to oscillate, and late-mounting cells, remounts, and
 * throttled background tabs all converge to the same derived count.
 *
 * Usage: render `items.slice(0, visibleCount)` (unmarked) in the container, plus
 * a ChipMeasureRow with ALL items, each chip wrapper carrying CHIP_OVERFLOW_ITEM.
 */
export function useChipOverflow({
  containerRef,
  measureRef,
  itemCount,
  gap = 4,
  reservedWidth = 0,
}: UseChipOverflowOptions) {
  const [visibleCount, setVisibleCount] = useState(itemCount)
  const [, setResizeTick] = useState(0)
  const observerRef = useRef<ResizeObserver>(undefined)
  const observedRef = useRef<HTMLElement | null>(null)

  // Recompute after every commit: all inputs are read fresh from the DOM and
  // setState bails when the value is unchanged, so steady state costs one cheap
  // read pass and no re-render.
  useLayoutEffect(() => {
    const container = containerRef.current
    const measurer = measureRef.current
    // Unmeasurable (hidden, mid-mount, jsdom): show everything, the container clips.
    if (!container || !measurer || container.clientWidth === 0) {
      setVisibleCount(itemCount)
      return
    }
    const chips = [...measurer.querySelectorAll(`[${CHIP_OVERFLOW_ITEM}]`)] as HTMLElement[]
    const widths = chips.map((chip) => chip.offsetWidth)
    const badgeWidth = measurer.querySelector<HTMLElement>(`[data-overflow-badge]`)?.offsetWidth ?? 0
    const { paddingLeft, paddingRight } = getComputedStyle(container)
    const available = container.clientWidth - parseFloat(paddingLeft) - parseFloat(paddingRight) - reservedWidth
    const fitCount = (limit: number) => {
      let used = 0
      let count = 0
      for (const width of widths) {
        const next = used + (count > 0 ? gap : 0) + width
        if (next > limit) break
        used = next
        count++
      }
      return count
    }
    const withoutBadge = fitCount(available)
    setVisibleCount(withoutBadge >= itemCount ? itemCount : fitCount(available - badgeWidth - gap))
  })

  // Container resizes (column drags) re-run the computation via a tick bump.
  // Attachment is re-checked every commit because containers can mount late
  // (the lazy tasks cell renders null until its data arrives) and a deps-gated
  // effect would never re-fire for a stable ref identity.
  useLayoutEffect(() => {
    const el = containerRef.current
    if (el === observedRef.current) return
    observerRef.current?.disconnect()
    observedRef.current = el
    if (!el) return
    const observer = new ResizeObserver(() => setResizeTick((tick) => tick + 1))
    observer.observe(el)
    observerRef.current = observer
  })

  useLayoutEffect(() => () => observerRef.current?.disconnect(), [])

  return { visibleCount }
}
